import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useToast } from '../ui/use-toast';
import { type UserFile, useUserVFS } from '../../hooks/useUserVFS';
import { ensureDir, ensureFile } from '../../utils/filesystem';
import {
  getContentTypeFromPath,
  isExternalModuleUrl,
  isTextContentType,
  normalizePackageRelativePath,
  normalizeWindowsPath,
  resolvePackageRelativePath,
  type MapleMarketModuleManifest,
} from '@/OS/utils/mapleMarketPackages';

type ModulePermission =
  | 'files:read'
  | 'files:write'
  | 'windows:open'
  | 'notifications:show'
  | 'network:proxy'
  | 'ai:maplept';

type OpenWindowHandler = (appId: string, title: string, filePath?: string, content?: string) => void;

type ModuleBridgeRequest = {
  __mapleHostBridge: true;
  type: 'request';
  id: string;
  method: string;
  params?: Record<string, unknown>;
};

type ModuleBridgeResponse = {
  __mapleHostBridge: true;
  type: 'response';
  id: string;
  ok: boolean;
  result?: unknown;
  error?: string;
};

type ModuleManifest = MapleMarketModuleManifest & {
  permissions?: ModulePermission[];
};

interface WebAppRunnerProps {
  rootPath?: string;
  onOpenWindow?: OpenWindowHandler;
}

const BRIDGE_BOOTSTRAP_SCRIPT = String.raw`<script>
(() => {
  if (window.MapleOS && window.MapleOS.__bridgeVersion === 1) {
    return;
  }

  const pending = new Map();
  let nextId = 1;

  const request = (method, params = {}) => new Promise((resolve, reject) => {
    const id = 'maple-host-' + nextId++;
    pending.set(id, { resolve, reject });
    window.parent.postMessage({
      __mapleHostBridge: true,
      type: 'request',
      id,
      method,
      params,
    }, '*');
  });

  const decorateFileResult = (result) => {
    if (!result || typeof result !== 'object') {
      return result;
    }

    try {
      Object.defineProperty(result, 'toString', {
        value() {
          return typeof result.content === 'string' ? result.content : '';
        },
        enumerable: false,
      });
      Object.defineProperty(result, Symbol.toPrimitive, {
        value() {
          return typeof result.content === 'string' ? result.content : '';
        },
        enumerable: false,
      });
    } catch {}

    return result;
  };

  const decorateListEntry = (entry) => {
    if (!entry || typeof entry !== 'object') {
      return entry;
    }

    const label = typeof entry.name === 'string' && entry.name
      ? entry.name
      : String(entry.path || '');

    try {
      Object.defineProperty(entry, 'toString', {
        value() {
          return label;
        },
        enumerable: false,
      });
      Object.defineProperty(entry, Symbol.toPrimitive, {
        value() {
          return label;
        },
        enumerable: false,
      });
    } catch {}

    return entry;
  };

  const decorateListResult = (result) => {
    if (!result || typeof result !== 'object') {
      return result;
    }

    const entries = Array.isArray(result.entries) ? result.entries.map(decorateListEntry) : [];
    const names = entries.map((entry) => typeof entry?.name === 'string' ? entry.name : String(entry?.path || ''));

    try {
      Object.defineProperty(result, 'join', {
        value(separator = ', ') {
          return names.join(separator);
        },
        enumerable: false,
      });
      Object.defineProperty(result, 'map', {
        value(callback) {
          return entries.map(callback);
        },
        enumerable: false,
      });
      Object.defineProperty(result, 'forEach', {
        value(callback) {
          return entries.forEach(callback);
        },
        enumerable: false,
      });
      Object.defineProperty(result, 'filter', {
        value(callback) {
          return entries.filter(callback);
        },
        enumerable: false,
      });
      Object.defineProperty(result, 'length', {
        get() {
          return entries.length;
        },
        enumerable: false,
      });
      Object.defineProperty(result, Symbol.iterator, {
        value: function* () {
          yield* entries;
        },
        enumerable: false,
      });
    } catch {}

    return result;
  };

  window.addEventListener('message', (event) => {
    const data = event.data;
    if (!data || data.__mapleHostBridge !== true || data.type !== 'response' || typeof data.id !== 'string') {
      return;
    }

    const entry = pending.get(data.id);
    if (!entry) {
      return;
    }

    pending.delete(data.id);
    if (data.ok) {
      entry.resolve(data.result);
    } else {
      entry.reject(new Error(data.error || 'MapleOS host request failed.'));
    }
  });

  const api = {
    __bridgeVersion: 1,
    app: {
      getInfo: () => request('app.getInfo'),
    },
    notifications: {
      show: (options) => request('notifications.show', options || {}),
    },
    windows: {
      open: (options) => request('windows.open', options || {}),
    },
    fs: {
      readFile: async (path) => decorateFileResult(await request('fs.readFile', { path })),
      writeFile: (path, content, options = {}) => request('fs.writeFile', { path, content, ...options }),
      mkdir: (path) => request('fs.mkdir', { path }),
      list: async (path) => decorateListResult(await request('fs.list', { path })),
    },
    host: {
      log: (...args) => request('host.log', { parts: args.map((value) => {
        if (typeof value === 'string') return value;
        try {
          return JSON.stringify(value);
        } catch {
          return String(value);
        }
      }) }),
    },
  };

  window.MapleOS = api;
  window.mapleOS = api;
  window.dispatchEvent(new CustomEvent('mapleos-ready', { detail: { version: 1 } }));
})();
</script>`;

const MAPLEOS_THEME_VARS = [
  '--background',
  '--foreground',
  '--card',
  '--card-foreground',
  '--popover',
  '--popover-foreground',
  '--primary',
  '--primary-foreground',
  '--secondary',
  '--secondary-foreground',
  '--muted',
  '--muted-foreground',
  '--accent',
  '--accent-foreground',
  '--border',
  '--input',
  '--ring',
  '--desktop',
  '--window-header',
  '--window-header-foreground',
  '--window-control-bg',
  '--window-control-bg-hover',
  '--gradient-window',
];

const normalizePath = (path: string) => normalizeWindowsPath(path);
const isJavaScriptPath = (path: string) => /\.(?:js|mjs)$/i.test(path);
const isStylesheetPath = (path: string) => /\.css$/i.test(path);

const injectDesktopBridge = (html: string) => {
  if (html.includes('__mapleHostBridge') || html.includes('__bridgeVersion === 1')) {
    return html;
  }

  if (/<\/head>/i.test(html)) {
    return html.replace(/<\/head>/i, `${BRIDGE_BOOTSTRAP_SCRIPT}</head>`);
  }

  if (/<body[^>]*>/i.test(html)) {
    return html.replace(/<body([^>]*)>/i, `<body$1>${BRIDGE_BOOTSTRAP_SCRIPT}`);
  }

  return `${BRIDGE_BOOTSTRAP_SCRIPT}${html}`;
};

const syncMapleOsThemeToFrame = (frame: HTMLIFrameElement | null) => {
  const iframeDocument = frame?.contentDocument;
  const iframeWindow = frame?.contentWindow;
  if (!iframeDocument || !iframeWindow) {
    return;
  }

  const hostRoot = (document.querySelector('.mapleos-root') as HTMLElement | null) || document.documentElement;
  const hostStyles = window.getComputedStyle(hostRoot);
  const iframeRoot = iframeDocument.documentElement;

  iframeRoot.classList.toggle('dark', hostRoot.classList.contains('dark'));
  iframeRoot.setAttribute('data-mapleos-theme', hostRoot.classList.contains('dark') ? 'dark' : 'light');

  for (const variableName of MAPLEOS_THEME_VARS) {
    const value = hostStyles.getPropertyValue(variableName);
    if (value.trim()) {
      iframeRoot.style.setProperty(variableName, value.trim());
    }
  }

  let themeStyle = iframeDocument.getElementById('mapleos-theme-bridge-style') as HTMLStyleElement | null;
  if (!themeStyle) {
    themeStyle = iframeDocument.createElement('style');
    themeStyle.id = 'mapleos-theme-bridge-style';
    iframeDocument.head.appendChild(themeStyle);
  }

  themeStyle.textContent = `
    :root {
      color-scheme: ${hostRoot.classList.contains('dark') ? 'dark' : 'light'};
    }

    html, body {
      background: hsl(var(--background)) !important;
      color: hsl(var(--foreground)) !important;
    }
  `;

  iframeWindow.dispatchEvent(new CustomEvent('mapleos-themechange', {
    detail: {
      mode: hostRoot.classList.contains('dark') ? 'dark' : 'light',
      variables: Object.fromEntries(
        MAPLEOS_THEME_VARS.map((variableName) => [variableName, iframeRoot.style.getPropertyValue(variableName)])
      ),
    },
  }));
};

function rewriteCssUrls(
  css: string,
  fromRelativePath: string,
  resolveAssetUrl: (targetPath: string, fromPath: string) => string,
) {
  return css
    .replace(/url\(([^)]+)\)/gi, (match, rawValue) => {
      const assetPath = String(rawValue || '').trim().replace(/^['"]|['"]$/g, '');
      if (!assetPath || isExternalModuleUrl(assetPath)) {
        return match;
      }

      return `url("${resolveAssetUrl(assetPath, fromRelativePath)}")`;
    })
    .replace(/@import\s+(?:url\()?\s*(['"])([^'"]+)\1\s*\)?/gi, (match, quote, rawPath) => {
      if (!rawPath || isExternalModuleUrl(rawPath)) {
        return match;
      }

      return `@import url("${resolveAssetUrl(rawPath, fromRelativePath)}")`;
    });
}

function rewriteJavaScriptImports(
  code: string,
  fromRelativePath: string,
  resolveAssetUrl: (targetPath: string, fromPath: string) => string,
) {
  return code
    .replace(/(import\s+(?:[^'"()]*?\sfrom\s*)?)(['"])([^'"]+)\2/g, (match, prefix, quote, rawPath) => {
      if (!rawPath || isExternalModuleUrl(rawPath)) {
        return match;
      }

      return `${prefix}${quote}${resolveAssetUrl(rawPath, fromRelativePath)}${quote}`;
    })
    .replace(/(export\s+[^'"()]*?\sfrom\s*)(['"])([^'"]+)\2/g, (match, prefix, quote, rawPath) => {
      if (!rawPath || isExternalModuleUrl(rawPath)) {
        return match;
      }

      return `${prefix}${quote}${resolveAssetUrl(rawPath, fromRelativePath)}${quote}`;
    })
    .replace(/(import\s*\(\s*)(['"])([^'"]+)\2(\s*\))/g, (match, prefix, quote, rawPath, suffix) => {
      if (!rawPath || isExternalModuleUrl(rawPath)) {
        return match;
      }

      return `${prefix}${quote}${resolveAssetUrl(rawPath, fromRelativePath)}${quote}${suffix}`;
    })
    .replace(/(new\s+URL\(\s*)(['"])([^'"]+)\2(\s*,\s*import\.meta\.url\s*\))/g, (match, prefix, quote, rawPath, suffix) => {
      if (!rawPath || isExternalModuleUrl(rawPath)) {
        return match;
      }

      return `${prefix}${quote}${resolveAssetUrl(rawPath, fromRelativePath)}${quote}${suffix}`;
    });
}

function rewriteSrcSet(
  value: string,
  fromRelativePath: string,
  resolveAssetUrl: (targetPath: string, fromPath: string) => string,
) {
  return value
    .split(',')
    .map((part) => {
      const trimmed = part.trim();
      if (!trimmed) {
        return trimmed;
      }

      const [rawUrl, descriptor] = trimmed.split(/\s+/, 2);
      if (!rawUrl || isExternalModuleUrl(rawUrl)) {
        return trimmed;
      }

      const nextUrl = resolveAssetUrl(rawUrl, fromRelativePath);
      return descriptor ? `${nextUrl} ${descriptor}` : nextUrl;
    })
    .join(', ');
}

function serializeDocument(documentNode: Document) {
  const doctype = documentNode.doctype
    ? `<!DOCTYPE ${documentNode.doctype.name}>\n`
    : '<!DOCTYPE html>\n';
  return `${doctype}${documentNode.documentElement.outerHTML}`;
}

const WebAppRunner: React.FC<WebAppRunnerProps> = ({ rootPath, onOpenWindow }) => {
  const { toast } = useToast();
  const { files, getFile, listFiles, createFile, updateFile, reloadFile } = useUserVFS();
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const generatedUrlsRef = useRef<string[]>([]);
  const [manifest, setManifest] = useState<ModuleManifest | null>(null);
  const [activeHtml, setActiveHtml] = useState('');
  const [isReady, setIsReady] = useState(false);

  const normalizedRootPath = useMemo(() => normalizePath(rootPath || ''), [rootPath]);

  const hasPermission = useCallback((permission: ModulePermission) => {
    return (manifest?.permissions || []).includes(permission);
  }, [manifest]);

  const resolveModuleFilePath = useCallback((value: unknown) => {
    const raw = String(value || '').trim();
    if (!raw) {
      throw new Error('Path is required.');
    }

    if (/^[a-z]:\\/i.test(raw) || /^[a-z]:$/i.test(raw)) {
      return normalizePath(raw);
    }

    return normalizePath(`${normalizedRootPath}\\${raw.replace(/^\\+/, '')}`);
  }, [normalizedRootPath]);

  const writeModuleFile = useCallback(async (filePath: string, content: string, contentType = 'text/plain') => {
    const normalized = normalizePath(filePath);
    const existing = getFile(normalized);

    if (existing) {
      await updateFile(normalized, content);
    } else {
      const lastSlash = normalized.lastIndexOf('\\');
      if (lastSlash > 2) {
        const dirPath = normalized.slice(0, lastSlash);
        await createFile(dirPath, '', true);
        await ensureDir(dirPath);
      }
      await createFile(normalized, content, false, contentType);
    }

    await ensureFile(normalized, content);
    return (await reloadFile(normalized)) ?? getFile(normalized);
  }, [createFile, getFile, reloadFile, updateFile]);

  const ensureModuleDir = useCallback(async (dirPath: string) => {
    const normalized = normalizePath(dirPath);
    await createFile(normalized, '', true);
    await ensureDir(normalized);
  }, [createFile]);

  const revokeGeneratedUrls = useCallback(() => {
    for (const url of generatedUrlsRef.current) {
      URL.revokeObjectURL(url);
    }
    generatedUrlsRef.current = [];
  }, []);

  const createGeneratedBlobUrl = useCallback((content: string, contentType: string) => {
    const url = URL.createObjectURL(new Blob([content], { type: contentType }));
    generatedUrlsRef.current.push(url);
    return url;
  }, []);

  const getModuleFilesMap = useCallback(() => {
    const prefix = `${normalizedRootPath}\\`;
    const moduleFiles = files.filter(
      (file) => !file.is_directory && file.file_path.startsWith(prefix),
    );
    const entries = new Map<string, UserFile>();

    for (const file of moduleFiles) {
      const relativePath = normalizePackageRelativePath(file.file_path.slice(prefix.length));
      entries.set(relativePath, file);
    }

    return entries;
  }, [files, normalizedRootPath]);

  const buildPackageHtml = useCallback((html: string, entryRelativePath: string, packageFiles: Map<string, UserFile>) => {
    const assetUrlCache = new Map<string, string>();
    const resolvingAssets = new Set<string>();

    const resolveAssetUrl = (targetPath: string, fromPath: string): string => {
      if (isExternalModuleUrl(targetPath)) {
        return targetPath;
      }

      const resolvedRelativePath = resolvePackageRelativePath(fromPath, targetPath);
      if (assetUrlCache.has(resolvedRelativePath)) {
        return assetUrlCache.get(resolvedRelativePath)!;
      }

      if (resolvingAssets.has(resolvedRelativePath)) {
        throw new Error(`Detected a cyclic package asset reference for ${resolvedRelativePath}.`);
      }

      const file = packageFiles.get(resolvedRelativePath);
      if (!file || file.is_directory) {
        throw new Error(`Missing package asset: ${resolvedRelativePath}`);
      }

      const contentType = file.content_type || getContentTypeFromPath(resolvedRelativePath);
      const content = file.content || '';

      if (!content) {
        throw new Error(`Package asset is empty: ${resolvedRelativePath}`);
      }

      if (!isTextContentType(contentType, resolvedRelativePath)) {
        if (content.startsWith('data:') || content.startsWith('blob:')) {
          assetUrlCache.set(resolvedRelativePath, content);
          return content;
        }

        const binaryUrl = createGeneratedBlobUrl(content, contentType);
        assetUrlCache.set(resolvedRelativePath, binaryUrl);
        return binaryUrl;
      }

      resolvingAssets.add(resolvedRelativePath);
      try {
        let rewrittenContent = content;

        if (isStylesheetPath(resolvedRelativePath)) {
          rewrittenContent = rewriteCssUrls(content, resolvedRelativePath, resolveAssetUrl);
        } else if (isJavaScriptPath(resolvedRelativePath)) {
          rewrittenContent = rewriteJavaScriptImports(content, resolvedRelativePath, resolveAssetUrl);
        }

        const blobUrl = createGeneratedBlobUrl(rewrittenContent, contentType);
        assetUrlCache.set(resolvedRelativePath, blobUrl);
        return blobUrl;
      } finally {
        resolvingAssets.delete(resolvedRelativePath);
      }
    };

    const documentNode = new DOMParser().parseFromString(html, 'text/html');

    documentNode.querySelectorAll('style').forEach((styleElement) => {
      styleElement.textContent = rewriteCssUrls(styleElement.textContent || '', entryRelativePath, resolveAssetUrl);
    });

    documentNode.querySelectorAll<HTMLElement>('[style]').forEach((element) => {
      const styleValue = element.getAttribute('style');
      if (!styleValue) {
        return;
      }

      element.setAttribute('style', rewriteCssUrls(styleValue, entryRelativePath, resolveAssetUrl));
    });

    documentNode.querySelectorAll('script').forEach((scriptElement) => {
      const source = scriptElement.getAttribute('src');
      if (source && !isExternalModuleUrl(source)) {
        scriptElement.setAttribute('src', resolveAssetUrl(source, entryRelativePath));
        return;
      }

      if (!source && (scriptElement.getAttribute('type') || '').toLowerCase() === 'module') {
        scriptElement.textContent = rewriteJavaScriptImports(scriptElement.textContent || '', entryRelativePath, resolveAssetUrl);
      }
    });

    documentNode.querySelectorAll('link[href]').forEach((linkElement) => {
      const href = linkElement.getAttribute('href');
      if (!href || isExternalModuleUrl(href)) {
        return;
      }

      linkElement.setAttribute('href', resolveAssetUrl(href, entryRelativePath));
    });

    documentNode.querySelectorAll('[src]').forEach((element) => {
      const source = element.getAttribute('src');
      if (!source || isExternalModuleUrl(source)) {
        return;
      }

      element.setAttribute('src', resolveAssetUrl(source, entryRelativePath));
    });

    documentNode.querySelectorAll('[poster]').forEach((element) => {
      const poster = element.getAttribute('poster');
      if (!poster || isExternalModuleUrl(poster)) {
        return;
      }

      element.setAttribute('poster', resolveAssetUrl(poster, entryRelativePath));
    });

    documentNode.querySelectorAll('[srcset]').forEach((element) => {
      const srcSet = element.getAttribute('srcset');
      if (!srcSet) {
        return;
      }

      element.setAttribute('srcset', rewriteSrcSet(srcSet, entryRelativePath, resolveAssetUrl));
    });

    return injectDesktopBridge(serializeDocument(documentNode));
  }, [createGeneratedBlobUrl]);

  const loadApp = useCallback(async (refreshFromSource = false) => {
    if (!normalizedRootPath) {
      revokeGeneratedUrls();
      setManifest(null);
      setActiveHtml('');
      setIsReady(false);
      return;
    }

    const manifestPath = `${normalizedRootPath}\\manifest.json`;
    const localManifestFile = getFile(manifestPath);
    const manifestFile = refreshFromSource && !localManifestFile
      ? (await reloadFile(manifestPath)) ?? localManifestFile
      : localManifestFile;

    if (!manifestFile?.content) {
      revokeGeneratedUrls();
      setManifest(null);
      setActiveHtml('');
      setIsReady(false);
      return;
    }

    try {
      const parsed = JSON.parse(manifestFile.content) as ModuleManifest;
      const htmlEntry = parsed.entry?.html || 'src/index.html';
      const htmlPath = normalizePath(`${normalizedRootPath}\\${htmlEntry}`);
      const localHtmlFile = getFile(htmlPath);
      const htmlFile = refreshFromSource && !localHtmlFile
        ? (await reloadFile(htmlPath)) ?? localHtmlFile
        : localHtmlFile;

      if (!htmlFile?.content) {
        throw new Error(`Missing HTML entry: ${htmlEntry}`);
      }

      revokeGeneratedUrls();
      const nextHtml = parsed.loader === 'package'
        ? buildPackageHtml(htmlFile.content, normalizePackageRelativePath(htmlEntry), getModuleFilesMap())
        : injectDesktopBridge(htmlFile.content);

      setManifest(parsed);
      setActiveHtml(nextHtml);
      setIsReady(true);
    } catch (error) {
      console.error('[WebAppRunner] Failed to load app', error);
      revokeGeneratedUrls();
      setManifest(null);
      setActiveHtml('');
      setIsReady(false);
    }
  }, [buildPackageHtml, getFile, getModuleFilesMap, normalizedRootPath, reloadFile, revokeGeneratedUrls]);

  useEffect(() => {
    void loadApp(true);
  }, [loadApp]);

  useEffect(() => {
    if (!normalizedRootPath) {
      return;
    }

    const hasChanged = files.some(file => file.file_path === normalizedRootPath || file.file_path.startsWith(`${normalizedRootPath}\\`));
    if (hasChanged) {
      void loadApp(false);
    }
  }, [files, loadApp, normalizedRootPath]);

  useEffect(() => () => revokeGeneratedUrls(), [revokeGeneratedUrls]);

  useEffect(() => {
    const frame = iframeRef.current;
    if (!frame || !isReady || !activeHtml) {
      return;
    }

    const applyTheme = () => syncMapleOsThemeToFrame(frame);
    applyTheme();

    const hostRoot = (document.querySelector('.mapleos-root') as HTMLElement | null) || document.documentElement;
    const observer = new MutationObserver(applyTheme);
    observer.observe(hostRoot, { attributes: true, attributeFilter: ['class', 'style'] });

    frame.addEventListener('load', applyTheme);
    return () => {
      observer.disconnect();
      frame.removeEventListener('load', applyTheme);
    };
  }, [activeHtml, isReady]);

  useEffect(() => {
    const handleBridgeMessage = async (event: MessageEvent<ModuleBridgeRequest>) => {
      const frame = iframeRef.current;
      if (!frame || !manifest || !isReady || event.source !== frame.contentWindow) {
        return;
      }

      const data = event.data;
      if (!data || data.__mapleHostBridge !== true || data.type !== 'request' || typeof data.id !== 'string') {
        return;
      }

      const respond = (ok: boolean, payload: unknown) => {
        if (!frame.contentWindow) {
          return;
        }

        const response: ModuleBridgeResponse = {
          __mapleHostBridge: true,
          type: 'response',
          id: data.id,
          ok,
          ...(ok ? { result: payload } : { error: String(payload || 'MapleOS host request failed.') }),
        };
        frame.contentWindow.postMessage(response, '*');
      };

      try {
        switch (data.method) {
          case 'app.getInfo': {
            respond(true, {
              name: manifest.name,
              version: manifest.version,
              description: manifest.description || '',
              rootPath: normalizedRootPath,
              permissions: manifest.permissions || [],
            });
            return;
          }
          case 'host.log': {
            const parts = Array.isArray(data.params?.parts) ? data.params.parts : [];
            console.log(`[WebAppRunner:${manifest.name}]`, ...parts);
            respond(true, { ok: true });
            return;
          }
          case 'notifications.show': {
            if (!hasPermission('notifications:show')) {
              throw new Error('Permission denied: notifications:show');
            }

            const title = String(data.params?.title || manifest.name).trim() || manifest.name;
            const description = String(data.params?.message || data.params?.description || '').trim();
            const rawVariant = String(
              data.params?.variant
              || data.params?.type
              || data.params?.status
              || data.params?.intent
              || data.params?.kind
              || ''
            ).trim().toLowerCase();
            const variant = ['destructive', 'error', 'danger', 'failure', 'failed'].includes(rawVariant)
              ? 'destructive'
              : 'default';
            toast({ title, description: description || undefined, variant });
            respond(true, { shown: true, variant });
            return;
          }

          case 'windows.open': {
            if (!hasPermission('windows:open')) {
              throw new Error('Permission denied: windows:open');
            }
            if (!onOpenWindow) {
              throw new Error('Window opening is unavailable in this session.');
            }

            const appId = String(data.params?.appId || '').trim();
            const title = String(data.params?.title || manifest.name).trim() || manifest.name;
            if (!appId) {
              throw new Error('appId is required.');
            }

            const filePath = typeof data.params?.filePath === 'string' ? data.params.filePath : undefined;
            const content = typeof data.params?.content === 'string' ? data.params.content : undefined;
            onOpenWindow(appId, title, filePath, content);
            respond(true, { opened: true });
            return;
          }
          case 'fs.readFile': {
            if (!hasPermission('files:read')) {
              throw new Error('Permission denied: files:read');
            }

            const targetPath = resolveModuleFilePath(data.params?.path);
            const file = (await reloadFile(targetPath)) ?? getFile(targetPath);
            if (!file || file.is_directory) {
              throw new Error(`File not found: ${targetPath}`);
            }

            respond(true, {
              path: targetPath,
              content: file.content || '',
              contentType: file.content_type || 'text/plain',
              updatedAt: file.updated_at || null,
            });
            return;
          }
          case 'fs.writeFile': {
            if (!hasPermission('files:write')) {
              throw new Error('Permission denied: files:write');
            }

            const targetPath = resolveModuleFilePath(data.params?.path);
            const content = String(data.params?.content ?? '');
            const contentType = typeof data.params?.contentType === 'string' && data.params.contentType.trim()
              ? data.params.contentType
              : 'text/plain';
            await writeModuleFile(targetPath, content, contentType);
            respond(true, { path: targetPath, saved: true });
            return;
          }
          case 'fs.mkdir': {
            if (!hasPermission('files:write')) {
              throw new Error('Permission denied: files:write');
            }

            const targetPath = resolveModuleFilePath(data.params?.path);
            await ensureModuleDir(targetPath);
            respond(true, { path: targetPath, created: true });
            return;
          }
          case 'fs.list': {
            if (!hasPermission('files:read')) {
              throw new Error('Permission denied: files:read');
            }

            const targetPath = resolveModuleFilePath(data.params?.path || normalizedRootPath);
            const entries = listFiles(targetPath);
            respond(true, {
              path: targetPath,
              entries: entries.map(entry => ({
                path: entry.file_path,
                name: entry.file_path.split('\\').filter(Boolean).pop() || entry.file_path,
                isDirectory: entry.is_directory,
                contentType: entry.content_type || null,
                updatedAt: entry.updated_at || null,
              })),
            });
            return;
          }
          default:
            throw new Error(`Unknown host method: ${data.method}`);
        }
      } catch (error) {
        respond(false, error instanceof Error ? error.message : 'MapleOS host request failed.');
      }
    };

    window.addEventListener('message', handleBridgeMessage as EventListener);
    return () => window.removeEventListener('message', handleBridgeMessage as EventListener);
  }, [ensureModuleDir, getFile, hasPermission, isReady, listFiles, manifest, normalizedRootPath, onOpenWindow, reloadFile, resolveModuleFilePath, toast, writeModuleFile]);

  if (!normalizedRootPath) {
    return <div className="flex h-full items-center justify-center bg-[#0b1220] text-sm text-slate-400">No app path provided.</div>;
  }

  if (!isReady || !activeHtml) {
    return <div className="flex h-full items-center justify-center bg-[#0b1220] text-sm text-slate-400">Loading app…</div>;
  }

  return (
    <div className="h-full w-full overflow-hidden bg-[#0b1220]">
      <iframe
        ref={iframeRef}
        title={manifest?.name || 'MapleOS App'}
        className="h-full w-full border-0 bg-white"
        srcDoc={activeHtml}
        sandbox="allow-scripts allow-same-origin allow-modals"
      />
    </div>
  );
};

export default WebAppRunner;
